import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';

import { BaseHttpService } from './base-http.service';
import { 
  LoginRequest, 
  LoginResponse, 
  User, 
  AuthResponse, 
  UpdateUsernameRequest, 
  ChangePasswordRequest,
  JwtPayload 
} from '../interfaces/auth.interface';
import { API_ENDPOINTS, STORAGE_KEYS } from '../constants/api.constants';

/**
 * Core authentication service
 * Handles login, logout, token management, and user session state
 */
@Injectable({
  providedIn: 'root'
})
export class AuthCoreService extends BaseHttpService {
  private isLoggedInSubject: BehaviorSubject<boolean>;
  public isLoggedIn$: Observable<boolean>;
  private redirectUrl: string = '/overview'; // Default redirect
  private userDataSubject: BehaviorSubject<User | null>;
  public userData$: Observable<User | null>;
  private readonly authRouter: Router;

  constructor(
    router: Router,
    http: HttpClient,
    @Inject(PLATFORM_ID) platformId: Object,
    private dialog: MatDialog
  ) {
    super(http, platformId);
    this.authRouter = router;
    
    // Initialize state from localStorage
    const initialLoggedInState = this.hasToken();
    const initialUserData = this.getUserDataFromStorage();
    
    this.isLoggedInSubject = new BehaviorSubject<boolean>(initialLoggedInState);
    this.isLoggedIn$ = this.isLoggedInSubject.asObservable();
    this.userDataSubject = new BehaviorSubject<User | null>(initialUserData);
    this.userData$ = this.userDataSubject.asObservable();
  }

  /**
   * Checks if authentication token exists
   */
  private hasToken(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      return token !== null && token !== '';
    }
    return false;
  }

  /**
   * Gets user data from localStorage
   */
  private getUserDataFromStorage(): User | null {
    if (isPlatformBrowser(this.platformId)) {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    }
    return null;
  }

  /**
   * Decodes JWT token payload
   */
  private decodeJwtPayload(token: string): JwtPayload | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload;
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }

  /**
   * Checks if the current token is expired
   */
  private isTokenExpired(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) return true;

    const payload = this.decodeJwtPayload(token);
    if (!payload) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  }

  /**
   * Stores user data in localStorage and updates state
   */
  private setUserData(token: string, username: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const payload = this.decodeJwtPayload(token);
    if (!payload) return;

    const userData: User = {
      id: parseInt(payload.sub),
      username: username,
      name: payload.name,
      jobs: payload.jobs || [],
      jobNames: payload.job_names || [],
      email: payload.email,
      created_at: payload.created_at,
      is_active: payload.is_active
    };

    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
    this.userDataSubject.next(userData);
  }

  /**
   * Performs user login
   */
  login(username: string, password: string): Observable<AuthResponse> {
    return new Observable(observer => {
      const loginRequest: LoginRequest = { username: username, password };
      
      this.post<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, loginRequest).subscribe({
        next: (response) => {
          if (isPlatformBrowser(this.platformId)) {
            // 1. Token mentése
            localStorage.setItem(STORAGE_KEYS.TOKEN, response.access_token);
            // 2. User adat beállítása
            this.setUserData(response.access_token, username);
          }
          
          // 3. Bejelentkezett állapot beállítása - EZ IS KELL hogy a guardok jól működjenek
          this.isLoggedInSubject.next(true);
          
          // 4. Kis delay hogy biztosan minden beálljon
          setTimeout(() => {
            const targetUrl = this.getRedirectUrl();
            this.authRouter.navigate([targetUrl]);
            this.redirectUrl = '/overview';
            
            observer.next({ success: true });
            observer.complete();
          }, 100); // 100ms delay
        },
        error: (error) => {
          this.isLoggedInSubject.next(false);
          
          // Inaktív fiók kezelése (a BaseHttpService.handleError Error objektummá alakítja)
          let errorMessage = 'Hibás felhasználónév vagy jelszó!';
          const msg = error?.message || '';
          if (msg.includes('Inaktív fiók')) {
            errorMessage = 'A fiókod inaktiválva lett. Kérlek, fordulj az adminisztrátorhoz.';
          }
          
          observer.next({ 
            success: false, 
            message: errorMessage 
          });
          observer.complete();
        }
      });
    });
  }

  /**
   * Logs out the user and clears session data
   */
  logout(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    }
    
    this.dialog.closeAll();
    this.isLoggedInSubject.next(false);
    this.userDataSubject.next(null);
    this.authRouter.navigate(['/login']);
  }

  /**
   * Checks if the user is currently authenticated
   */
  isAuthenticated(): boolean {
    const hasToken = this.hasToken();
    
    if (!hasToken) {
      this.isLoggedInSubject.next(false);
      return false;
    }
    
    if (this.isTokenExpired()) {
      this.logout();
      return false;
    }
    
    // Synchronize state if needed
    if (hasToken !== this.isLoggedInSubject.value) {
      this.isLoggedInSubject.next(hasToken);
    }
    
    return hasToken;
  }

  /**
   * Sets the URL to redirect to after login
   */
  setRedirectUrl(url: string): void {
    this.redirectUrl = url;
  }

  /**
   * Gets the URL to redirect to after login
   */
  getRedirectUrl(): string {
    return this.redirectUrl;
  }

  /**
   * Gets the current user data
   */
  getCurrentUser(): User | null {
    return this.userDataSubject.value;
  }

  /**
   * Updates the current user's username
   */
  updateUsername(newUsername: string): Observable<AuthResponse> {
    if (!this.hasValidToken()) {
      return this.returnErrorWhenNoToken();
    }

    return new Observable(observer => {
      const request: UpdateUsernameRequest = { new_username: newUsername };
      
      this.put<AuthResponse>(API_ENDPOINTS.AUTH.UPDATE_USERNAME, request).subscribe({
        next: (response) => {
          // Update local user data
          const currentUser = this.getCurrentUser();
          if (currentUser && isPlatformBrowser(this.platformId)) {
            
            currentUser.username = newUsername; // For backward compatibility
            localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(currentUser));
            this.userDataSubject.next(currentUser);
          }
          
          observer.next({ success: true, message: response.message });
          observer.complete();
        },
        error: (error) => {
          observer.next({ 
            success: false, 
            message: error.message || 'Hiba történt a felhasználónév frissítése során!' 
          });
          observer.complete();
        }
      });
    });
  }

  /**
   * Changes the current user's password
   */
  changePassword(currentPassword: string, newPassword: string): Observable<AuthResponse> {
    if (!this.hasValidToken()) {
      return this.returnErrorWhenNoToken();
    }

    const request: ChangePasswordRequest = { 
      current_password: currentPassword, 
      new_password: newPassword 
    };

    return new Observable(observer => {
      this.put<AuthResponse>(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, request).subscribe({
        next: (response) => {
          observer.next({ success: true, message: response.message });
          observer.complete();
        },
        error: (error) => {
          observer.next({ 
            success: false, 
            message: error.message || 'Hiba történt a jelszó változtatása során!' 
          });
          observer.complete();
        }
      });
    });
  }

  /**
   * Returns the current user's roles
   */
  getCurrentUserRoles(): string[] {
    const userData = this.getCurrentUser();
    return userData ? userData.jobs : [];
  }

  /**
   * Returns the current user's primary role (first role) for backwards compatibility
   */
  getCurrentUserRole(): string | null {
    const roles = this.getCurrentUserRoles();
    return roles.length > 0 ? roles[0] : null;
  }

  /**
   * Checks if the user has a specific role
   */
  hasRole(role: string): boolean {
    return this.getCurrentUserRoles().includes(role);
  }

  /**
   * Checks if the user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    const currentRoles = this.getCurrentUserRoles();
    return roles.some(role => currentRoles.includes(role));
  }

  /**
   * Checks if the user is an administrator
   */
  isAdmin(): boolean {
    return this.hasRole('admin');
  }

  /**
   * Checks if the user has manager privileges
   */
  isManager(): boolean {
    return this.hasAnyRole(['admin', 'sales_manager', 'deputy-CEO']);
  }

  /**
   * Checks if the user is a call center employee
   */
  isCallCenterEmployee(): boolean {
    return this.hasRole('call_center');
  }

  /**
   * Checks if the user is a Key Account Manager
   */
  isKAM(): boolean {
    return this.hasRole('kam');
  }

  /**
   * Checks if the user works in the investment division
   */
  isInvestmentDivision(): boolean {
    return this.hasRole('investment');
  }

  /**
   * Checks if the user is a sales support manager
   */
  isSalesSupportManager(): boolean {
    return this.hasAnyRole(['sales_manager', 'deputy-CEO']);
  }
}